import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const readMinutes = (p) => Math.max(1, Math.round((p.body || "").split(/\s+/).filter(Boolean).length / 200));

const FILTERS = [
  { id: "all", label: "All lengths" },
  { id: "short", label: "Short — under 4 min" },
  { id: "medium", label: "Medium — 4 to 8 min" },
  { id: "long", label: "Long — 9 min & beyond" },
];

const matches = (p, f) => {
  const m = readMinutes(p);
  if (f === "short") return m <= 3;
  if (f === "medium") return m >= 4 && m <= 8;
  if (f === "long") return m >= 9;
  return true;
};

export default function SectionPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    setData(null);
    setMissing(false);
    setFilter("all");
    api.get(`/sections/${slug}/posts`)
      .then(({ data }) => setData(data))
      .catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <div className="min-h-screen bg-[#070709] flex flex-col items-center justify-center gap-6" data-testid="section-not-found">
        <p className="font-serif text-3xl text-[#ede8e1]">This chamber does not exist.</p>
        <Link to="/" className="font-mono text-xs tracking-[0.25em] uppercase text-[#581313]">Return to the Vault</Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#070709] flex items-center justify-center">
        <p className="font-mono text-xs tracking-[0.4em] uppercase text-[#78716c] flicker">Unsealing…</p>
      </div>
    );
  }

  const { section } = data;
  const posts = slug === "essays" ? data.posts.filter((p) => matches(p, filter)) : data.posts;

  return (
    <div className="relative min-h-screen" data-testid="section-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-24 relative z-10">
        <Link to="/" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-[#78716c] hover:text-[#ede8e1] transition-colors" data-testid="section-back-link">
          <ArrowLeft size={14} /> The Vault
        </Link>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="mt-8 mb-10">
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#581313] mb-4">{section.eyebrow}</p>
          <h1 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#ede8e1]" data-testid="section-title">
            {section.title}
          </h1>
          <p className="mt-4 max-w-xl text-[#a8a29e] font-light">{section.description}</p>
        </motion.div>
        {slug === "essays" && (
          <div className="flex flex-wrap gap-2 mb-10" data-testid="reading-time-filter">
            {FILTERS.map((f) => (
              <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`filter-${f.id}`}
                className={`font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors ${
                  filter === f.id
                    ? "bg-[#581313] border-[#581313]/60 text-[#ede8e1]"
                    : "bg-[#0d0e12] border-[#232530] text-[#a8a29e] hover:border-[#581313]/50"
                }`}>
                {f.label}
              </button>
            ))}
          </div>
        )}
        <div className="border-t border-[#232530]">
          {posts.length === 0 && (
            <p className="py-16 font-mono text-xs tracking-[0.3em] uppercase text-[#78716c]" data-testid="section-empty">Nothing rests here yet.</p>
          )}
          {posts.map((p, i) => {
            const inner = (
              <div className="group flex flex-col md:flex-row md:items-center gap-4 md:gap-10 py-8 border-b border-[#232530] hover:bg-[#0d0e12] transition-colors px-2 md:px-4">
                <span className="font-mono text-xs text-[#78716c] w-10 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <div className="flex-1">
                  <h2 className="font-serif text-xl sm:text-2xl text-[#ede8e1] group-hover:translate-x-2 transition-transform duration-300">{p.title}</h2>
                  {p.excerpt && <p className="mt-1 text-sm text-[#a8a29e] font-light line-clamp-2">{p.excerpt}</p>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716c]">{readMinutes(p)} min</span>
                  {p.featured && <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#581313]">Featured</span>}
                  <ArrowUpRight size={18} className="text-[#581313]" />
                </div>
              </div>
            );
            return (
              <motion.div key={p.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
                {p.external_url
                  ? <a href={p.external_url} target="_blank" rel="noopener noreferrer" data-testid={`section-post-${p.slug}`}>{inner}</a>
                  : <Link to={`/post/${p.slug}`} data-testid={`section-post-${p.slug}`}>{inner}</Link>}
              </motion.div>
            );
          })}
        </div>
      </main>
      <Footer />
    </div>
  );
}
